import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { FleetSection } from '../../../../shared/sso-workspace/fleet-page'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { OrgPicker } from '../../../../shared/org-search/org-picker'
import { testSend } from '../../../../api-new/state/notify'
import { CHANNELS, channelLabel, priorityLabel } from '../../../../shared/notify/words'
import { useNotifyOpsAccess } from './access'
import { timelinePath } from './paths'

const PRIORITIES = ['low', 'normal', 'high', 'critical']

// The send-a-test-notification tool (C-4 POST /notify/test-send, SYS_NOTIFY
// update, audited): raises a real `notify.test` event through the normal
// pipeline to one live member of one organization (title at most 120
// characters, message at most 400, as notify caps them). Preferences are
// bypassed, budgets are not. The answer's outbox id opens the timeline,
// where every hop (and, on test environments, the captured payloads) shows.
export const TestSendTab = () => {
  const access = useNotifyOpsAccess()
  const [organizationId, setOrganizationId] = useState('')
  const [loginId, setLoginId] = useState('')
  const [channels, setChannels] = useState(['inapp', 'push', 'email'])
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [link, setLink] = useState('')
  const [priority, setPriority] = useState('normal')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [error, failure, setError] = useErrorLine()

  if (!access.canUpdate) {
    return (
      <FleetSection id="nf-test-send" title="Test send">
        <p className="sw-hint m-0">You need SYS_NOTIFY update to send a test notification, and it can't be sent while impersonating.</p>
      </FleetSection>
    )
  }

  const login = Number(loginId)
  const org = Number(organizationId)
  const linkProblem = link.trim() && !/^edxr:\/\//.test(link.trim()) ? 'A link must start with edxr:// (for a course: edxr://content?id=<course id>).' : null
  const ready = Number.isInteger(login) && login > 0 && Number.isInteger(org) && org > 0 && channels.length > 0 && !linkProblem && !busy

  const toggle = c => setChannels(list => (list.includes(c) ? list.filter(x => x !== c) : CHANNELS.filter(x => x === c || list.includes(x))))

  const send = async e => {
    e.preventDefault()
    if (!ready) return
    const payload = { organizationId: org, loginId: login, channels: CHANNELS.filter(c => channels.includes(c)), priority }
    if (title.trim()) payload.title = title.trim()
    if (body.trim()) payload.body = body.trim()
    if (link.trim()) payload.link = link.trim()
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      setResult((await testSend(payload)) || {})
    } catch (err) {
      setError(serverMessage(err, "The test notification couldn't be sent."), err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <FleetSection id="nf-test-send" title="Test send"
      lede="Send a real test notification, through the whole pipeline, to one member of one organization. Their preferences are ignored; budgets still apply. On test environments push and email are captured, not sent.">
      <form onSubmit={send} style={{ maxWidth: '36rem' }}>
        <div className="sw-field mb-2">
          <OrgPicker id="nf-ts-org" label="Organization" value={organizationId} onChange={setOrganizationId} required />
        </div>
        <div className="sw-field mb-2">
          <label className="sw-label" htmlFor="nf-ts-login">Login id</label>
          <input id="nf-ts-login" className="form-control" inputMode="numeric" value={loginId} onChange={e => setLoginId(e.target.value.replace(/\D/g, ''))} />
          <span className="sw-hint small">The login must be a live member of the organization.</span>
        </div>
        <fieldset className="mb-2">
          <legend className="sw-label">Channels</legend>
          {CHANNELS.map(c =>
            <div className="form-check form-check-inline" key={c}>
              <input className="form-check-input" type="checkbox" id={`nf-ts-${c}`} checked={channels.includes(c)} onChange={() => toggle(c)} />
              <label className="form-check-label" htmlFor={`nf-ts-${c}`}>{channelLabel(c)}</label>
            </div>)}
        </fieldset>
        <div className="sw-field mb-2">
          <label className="sw-label" htmlFor="nf-ts-title">Title</label>
          <input id="nf-ts-title" className="form-control" maxLength={120} placeholder="Test notification" value={title} onChange={e => setTitle(e.target.value)} />
        </div>
        <div className="sw-field mb-2">
          <label className="sw-label" htmlFor="nf-ts-body">Message</label>
          <textarea id="nf-ts-body" className="form-control" rows={2} maxLength={400} value={body} onChange={e => setBody(e.target.value)} />
        </div>
        <div className="sw-field mb-2">
          <label className="sw-label" htmlFor="nf-ts-link">Link (optional)</label>
          <input id="nf-ts-link" className={`form-control sw-mono${linkProblem ? ' is-invalid' : ''}`} placeholder="edxr://content?id=1234" value={link} onChange={e => setLink(e.target.value)} />
          {linkProblem && <span className="sw-required small" role="alert">{linkProblem}</span>}
        </div>
        <div className="sw-field mb-3">
          <label className="sw-label" htmlFor="nf-ts-priority">Priority</label>
          <select id="nf-ts-priority" className="form-select" value={priority} onChange={e => setPriority(e.target.value)}>
            {PRIORITIES.map(p => <option key={p} value={p}>{priorityLabel(p)}</option>)}
          </select>
        </div>
        {error && <p className="text-danger small" role="alert">{error}</p>}
        {error && <ErrorReference error={failure} className="mb-2" />}
        <button type="submit" className="btn sw-btn-primary" disabled={!ready}>{busy ? 'Sending…' : 'Send test'}</button>
      </form>
      {result && result.outboxId &&
        <p className="text-success small mt-3" role="status">
          {'Raised. '}
          <Link to={timelinePath(result.outboxId, true)}>Open its timeline</Link>
          <span className="sw-hint sw-mono ms-2">{result.outboxId}</span>
        </p>}
    </FleetSection>
  )
}
